import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DATE_PRESETS, presetRange, regionalToday } from "@/components/bi/FilterBar";
import { getFormatDefaults } from "@/lib/chartTheme";
import { formatDateRegional } from "@/context/RegionalContext";
import { Search, ChevronDown, X, CalendarDays, Check, RotateCcw, FlipHorizontal2, Ban, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { smartSortValues, humanizeField } from "@/lib/filterValues";
import {
  readSelection, writeSelection, valueLabel, matchesSearch,
  selectAllVisible, clearVisible, invertVisible, visibleState,
} from "@/lib/filterSelection";
import { filterStyleVars, resolveFilterStyle, DEFAULT_FILTER_STYLE } from "@/lib/filterStyle";
import { FONT_FAMILIES } from "@/components/bi/FontStyleControls";

export const FILTER_WIDGET_TYPES = ["combo_box", "list_box", "date_picker"];

// Friendly title for any filter widget: explicit title → per-visual field label → humanized field.
export function filterWidgetTitle(w) {
  const o = w.config?.options || {};
  const dim = w.config?.dimensions?.[0] || {};
  return o.filter_label || dim.label || humanizeField(dim.field || "") || "Filter";
}

// ---------------------------------------------------------------------------
// Value state -> backend filters (used by Builder & Viewer). Filters carry
// __src so the interaction matrix (config.interactions) gates listeners.
// ---------------------------------------------------------------------------
export function filterWidgetToFilters(w, val) {
  const field = w.config?.dimensions?.[0]?.field;
  if (!field || val == null) return [];
  const base = { dataset_id: w.dataset_id, field, __src: w.id };
  if (w.type === "combo_box" || w.type === "list_box" || w.type === "slicer") {
    const { values, exclude } = readSelection(val);
    if (!values.length) return [];
    return [{ ...base, op: exclude ? "not_in" : "in", value: values }];
  }
  if (w.type === "date_picker") {
    const { mode, start, end, preset, n } = val || {};
    if (mode === "relative") {
      if (preset === "last_n") {
        const days = Number(n) || 0;
        if (!days) return [];
        const iso = (d) => d.toISOString().slice(0, 10);
        const today = regionalToday();
        return [{ ...base, op: "between", value: [iso(new Date(today - days * 86400000)), iso(today)] }];
      }
      const r = preset && presetRange(preset);
      return r ? [{ ...base, op: "between", value: r }] : [];
    }
    if (mode === "single") return start ? [{ ...base, op: "between", value: [start, start] }] : [];
    if (mode === "before") return start ? [{ ...base, op: "lte", value: start }] : [];
    if (mode === "after") return start ? [{ ...base, op: "gte", value: start }] : [];
    if (start && end) return [{ ...base, op: "between", value: [start, end] }];
    if (start) return [{ ...base, op: "gte", value: start }];
    if (end) return [{ ...base, op: "lte", value: end }];
  }
  return [];
}

export function filterWidgetSummary(w, val) {
  if (w.type === "date_picker") {
    const { mode, start, end, preset, n } = val || {};
    if (mode === "relative" && preset) return preset === "last_n" ? `Last ${n || "…"} days` : (DATE_PRESETS.find((p) => p.value === preset)?.label || preset);
    const fd = (v) => { const f = getFormatDefaults().dateFormat; return (v && f && formatDateRegional(v, f)) || v; };
    if (mode === "single") return fd(start) || "All dates";
    if (mode === "before") return start ? `Before ${fd(start)}` : "All dates";
    if (mode === "after") return start ? `After ${fd(start)}` : "All dates";
    if (start || end) return `${fd(start) || "…"} → ${fd(end) || "…"}`;
    return "All dates";
  }
  const { values: arr, exclude } = readSelection(val);
  if (!arr.length) return "All";
  const pre = exclude ? "Excluding " : "";
  if (arr.length === 1) return `${pre}${valueLabel(arr[0])}`;
  return `${pre}${arr.length} selected`;
}

// ---------------------------------------------------------------------------
// Shared distinct-values hook (cascades: other active filters narrow the list)
// ---------------------------------------------------------------------------
function useDistinct(widget, publicToken, globalFilters, sort, sortKind) {
  const field = widget.config?.dimensions?.[0]?.field;
  const [values, setValues] = useState([]);
  const others = (globalFilters || []).filter((f) => f.field !== field);
  const othersKey = JSON.stringify(others);
  useEffect(() => {
    if (!field || !widget.dataset_id) return;
    const req = publicToken
      ? api.post("/public/distinct", { token: publicToken, dataset_id: widget.dataset_id, field, filters: others })
      : api.post(`/datasets/${widget.dataset_id}/distinct`, { field, filters: others });
    req.then(({ data }) => setValues((data.values || []).map((v) => (v == null ? "" : String(v))))).catch(() => setValues([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, widget.dataset_id, publicToken, othersKey]);
  return useMemo(() => smartSortValues(values, sort || "asc", sortKind || "auto"), [values, sort, sortKind]);
}

function FilterTitle({ widget, className }) {
  const o = widget.config?.options || {};
  const st = resolveFilterStyle(o);
  if (o.show_label === false || st.text.show === false) return null;
  return <div data-testid="filter-widget-title" className={cn("fw-label truncate shrink-0", className)}>{filterWidgetTitle(widget)}</div>;
}

// Quick actions act on the VISIBLE rows only; selections hidden by the search survive.
function QuickActions({ mode, onAll, onClear, onInvert, count, total, state = "none", exclude, onExclude }) {
  return (
    <div className="flex items-center gap-1 px-1 py-1 border-b border-[color:var(--glass-border)]">
      {mode !== "single" && (
        <button data-testid="fw-select-all" onClick={onAll} data-state={state} title="Select everything the search shows"
          className="fw-btn flex items-center gap-1">
          <span data-on={state === "all"} className="fw-check w-3 h-3 rounded flex items-center justify-center shrink-0">
            {state === "all" ? <Check className="w-2 h-2 text-white" /> : state === "some" ? <Minus className="w-2 h-2" /> : null}
          </span>
          Select all
        </button>
      )}
      <button data-testid="fw-clear" onClick={onClear} className="fw-btn">Clear</button>
      {mode !== "single" && (
        <button data-testid="fw-invert" onClick={onInvert} title="Invert selection" className="fw-btn"><FlipHorizontal2 className="w-3 h-3" /></button>
      )}
      {onExclude && (
        <button data-testid="fw-exclude-toggle" onClick={() => onExclude(!exclude)} data-on={!!exclude}
          title={exclude ? "Excluding the selected values (NOT IN)" : "Keeping only the selected values (IN)"}
          className={cn("fw-btn flex items-center gap-1", exclude && "!text-[color:var(--danger)] font-semibold")}>
          <Ban className="w-3 h-3" />{exclude ? "Exclude" : "Include"}
        </button>
      )}
      <span className="ml-auto text-[9px] text-[color:var(--ink-3)] pr-1">{count}/{total}</span>
    </div>
  );
}

function ValueList({ values, sel, mode, toggle, checkboxes = true, testid, maxH = "max-h-56", allOption = null }) {
  const [limit, setLimit] = useState(300);
  const shown = values.slice(0, limit);
  return (
    <div className={cn("overflow-auto fw-list", maxH)} data-testid={testid}>
      {allOption && (
        <button data-nodrag data-testid="fw-option-all" data-on={sel.length === 0} onClick={allOption}
          className="fw-option w-full flex items-center gap-2 px-2 py-1 text-xs rounded-md text-left">
          <span className="w-3.5 shrink-0">{sel.length === 0 ? <Check className="w-3 h-3" /> : null}</span>
          <span className="truncate">All</span>
        </button>
      )}
      {shown.map((v) => {
        const on = sel.includes(v);
        return (
          <button key={String(v)} data-nodrag data-on={on} data-testid={`fw-option-${String(v)}`} aria-pressed={on} onClick={() => toggle(v)}
            className={cn("fw-option w-full flex items-center gap-2 px-2 py-1 text-xs rounded-md text-left", on && "font-medium")}>
            {mode !== "single" && checkboxes
              ? <span data-on={on} className="fw-check w-3.5 h-3.5 rounded flex items-center justify-center shrink-0">{on && <Check className="w-2.5 h-2.5 text-white" />}</span>
              : <span className="w-3.5 shrink-0">{on ? <Check className="w-3 h-3" /> : null}</span>}
            <span className={cn("truncate", (String(v) === "" || String(v).trim() === "" || String(v).startsWith("(")) && "italic text-[color:var(--ink-3)]")}>{valueLabel(v)}</span>
          </button>
        );
      })}
      {values.length > limit && (
        <button data-testid="fw-load-more" onClick={() => setLimit((l) => l + 300)} className="w-full text-[10px] py-1 text-[color:var(--brand-ocean)] hover:underline">Show more ({values.length - limit} left)</button>
      )}
      {!values.length && <p className="text-[10px] text-[color:var(--ink-3)] px-2 py-2">No values</p>}
    </div>
  );
}

// Shared dropdown body: search / quick actions / list / apply-cancel.
// Every edit (including Clear and Include/Exclude) stays pending while applyMode is on.
function DropdownBody({ o, mode, values, sel, setSel, toggle, applyMode, onApply, onCancel, allOption, testPrefix,
  exclude = false, setExclude = null }) {
  const [q, setQ] = useState("");
  const filtered = q ? values.filter((v) => matchesSearch(v, q)) : values;
  return (
    <>
      {o.search !== false && (
        <div className="relative mb-1">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid={`${testPrefix}-search`} autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-7 pl-6 text-xs bg-[color:var(--surface-2)]" />
        </div>
      )}
      <QuickActions mode={mode} count={sel.length} total={values.length} state={visibleState(sel, filtered)}
        exclude={exclude} onExclude={o.exclude === false ? null : setExclude}
        onAll={() => setSel(visibleState(sel, filtered) === "all" ? clearVisible(sel, filtered) : selectAllVisible(sel, filtered))}
        onClear={() => setSel([])}
        onInvert={() => setSel(invertVisible(sel, filtered))} />
      <ValueList values={filtered} sel={sel} mode={mode} toggle={toggle} testid={`${testPrefix}-list`} allOption={allOption} />
      {applyMode && (
        <div className="flex justify-end gap-1 pt-1 border-t border-[color:var(--glass-border)]">
          <button data-testid={`${testPrefix}-cancel`} className="fw-btn" onClick={onCancel}>Cancel</button>
          <button data-testid={`${testPrefix}-apply`} data-primary="true" className="fw-btn" onClick={onApply}>Apply</button>
        </div>
      )}
    </>
  );
}

const TriggerButton = React.forwardRef(function TriggerButton({ testid, active, summary, onClear, showClear, extraClass, ...rest }, ref) {
  return (
    <button ref={ref} {...rest} data-testid={testid} data-active={!!active}
      className={cn("fw-selection w-full flex items-center justify-between gap-2 text-left min-w-0", extraClass)}>
      <span className="truncate">{summary}</span>
      <span className="flex items-center gap-1 shrink-0">
        {showClear && <span data-testid={`${testid}-clear-inline`} role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onClear(); }} className="p-0.5 rounded hover:bg-black/5"><X className="fw-icon" /></span>}
        <ChevronDown className="fw-icon" />
      </span>
    </button>
  );
});

// ---------------------------------------------------------------------------
// Slicer (legacy "Filter" widget, type "slicer") — rebuilt: friendly title, single/multi,
// Apply/Clear, chronological sort, style zones. Value: string (single) or string[] (multi).
// ---------------------------------------------------------------------------
export function SlicerFilterWidget({ widget, publicToken, value, onChange, globalFilters = [] }) {
  const field = widget.config?.dimensions?.[0]?.field;
  const o = widget.config?.options || {};
  const mode = o.selection_mode === "multi" ? "multi" : "single";
  const values = useDistinct(widget, publicToken, globalFilters, o.sort || "asc", o.sort_kind);
  const [open, setOpen] = useState(false);
  const applyMode = !!o.apply_mode;
  const [pending, setPending] = useState(null);   // {values, exclude} while staging
  const vars = filterStyleVars(o);
  const committed = readSelection(value);
  const cur = applyMode && pending ? pending : committed;
  const sel = cur.values;
  const commit = (arr, ex) => onChange && onChange(field, mode === "multi" ? writeSelection(arr, ex) : (arr.length ? writeSelection([arr[0]], ex) : null));
  const setSel = (arr, ex = cur.exclude) => (applyMode ? setPending({ values: arr, exclude: ex }) : commit(arr, ex));
  const toggle = (v) => {
    if (mode === "single") { setSel(sel.includes(v) ? [] : [v]); if (!applyMode) setOpen(false); return; }
    setSel(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  };
  const applyPending = () => { if (pending) commit(pending.values, pending.exclude); setPending(null); };
  if (!field) return <div className="h-full w-full flex items-center justify-center text-xs text-[color:var(--ink-3)]">Pick a field for this filter</div>;
  const summary = committed.values.length === 0 ? (o.placeholder || "All") : filterWidgetSummary(widget, value);
  const inline = o.display === "list";
  return (
    <div className="fw-root h-full w-full flex flex-col justify-center px-1 gap-1 min-h-0" style={vars} data-nodrag data-testid="slicer-widget">
      <FilterTitle widget={widget} />
      {inline ? (
        <div className="fw-dropdown flex-1 min-h-0 overflow-hidden p-1" style={vars}>
          <DropdownBody o={{ ...o, search: o.search === true }} mode={mode} values={values} sel={sel} setSel={setSel} toggle={toggle}
            exclude={cur.exclude} setExclude={(ex) => setSel(sel, ex)}
            applyMode={applyMode} onApply={applyPending} onCancel={() => setPending(null)}
            allOption={() => { setSel([]); }} testPrefix="slicer" />
        </div>
      ) : (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); setPending(null); }}>
          <PopoverTrigger asChild>
            <TriggerButton testid="slicer-select" active={committed.values.length > 0} summary={summary} showClear={committed.values.length > 0 && o.show_clear !== false} onClear={() => commit([], false)} />
          </PopoverTrigger>
          <PopoverContent align="start" className="fw-dropdown w-64 p-1.5" style={vars} data-testid="slicer-popover">
            <DropdownBody o={o} mode={mode} values={values} sel={sel} setSel={setSel} toggle={toggle}
              exclude={cur.exclude} setExclude={(ex) => setSel(sel, ex)}
              applyMode={applyMode} onApply={() => { applyPending(); setOpen(false); }} onCancel={() => { setPending(null); setOpen(false); }}
              allOption={() => { setSel([]); if (!applyMode) setOpen(false); }} testPrefix="slicer" />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Combo Box Filter — dropdown popover with search / select-all / multi-select
// ---------------------------------------------------------------------------
export function ComboBoxFilterWidget({ widget, publicToken, value, onChange, globalFilters = [] }) {
  const o = widget.config?.options || {};
  const mode = o.selection_mode === "single" ? "single" : "multi";
  const values = useDistinct(widget, publicToken, globalFilters, o.sort || "asc", o.sort_kind);
  const [open, setOpen] = useState(false);
  const applyMode = !!o.apply_mode;
  const [pending, setPending] = useState(null); // {values, exclude} apply-mode staging
  const vars = filterStyleVars(o);
  const committed = readSelection(value);
  const cur = applyMode && pending ? pending : committed;
  const sel = cur.values;
  const commit = (arr, ex) => onChange(writeSelection(arr, ex));
  const setSel = (arr, ex = cur.exclude) => (applyMode ? setPending({ values: arr, exclude: ex }) : commit(arr, ex));
  const toggle = (v) => {
    if (mode === "single") { setSel(sel.includes(v) ? [] : [v]); if (!applyMode) setOpen(false); return; }
    setSel(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  };
  const summary = filterWidgetSummary(widget, value);
  return (
    <div className="fw-root h-full w-full flex flex-col justify-center px-1 gap-1" style={vars} data-nodrag>
      <FilterTitle widget={widget} />
      <Popover open={open} onOpenChange={(v) => { setOpen(v); setPending(null); }}>
        <PopoverTrigger asChild>
          <TriggerButton testid="combo-filter-trigger" active={committed.values.length > 0} summary={summary === "All" ? (o.placeholder || "All") : summary}
            showClear={committed.values.length > 0} onClear={() => commit([], false)} />
        </PopoverTrigger>
        <PopoverContent align="start" className="fw-dropdown w-64 p-1.5" style={vars} data-testid="combo-filter-popover">
          <DropdownBody o={o} mode={mode} values={values} sel={sel} setSel={setSel} toggle={toggle}
            exclude={cur.exclude} setExclude={(ex) => setSel(sel, ex)}
            applyMode={applyMode} onApply={() => { if (pending) commit(pending.values, pending.exclude); setPending(null); setOpen(false); }}
            onCancel={() => { setPending(null); setOpen(false); }}
            testPrefix="combo-filter" />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// List Box Filter — always-visible list with checkboxes / select-all / search
// ---------------------------------------------------------------------------
export function ListBoxFilterWidget({ widget, publicToken, value, onChange, globalFilters = [] }) {
  const o = widget.config?.options || {};
  const mode = o.selection_mode === "single" ? "single" : "multi";
  const values = useDistinct(widget, publicToken, globalFilters, o.sort || "asc", o.sort_kind);
  const [q, setQ] = useState("");
  const [onlySel, setOnlySel] = useState(false);
  const vars = filterStyleVars(o);
  const committed = readSelection(value);
  const sel = committed.values;
  const exclude = committed.exclude;
  const commit = (arr, ex = exclude) => onChange(writeSelection(arr, ex));
  let filtered = q ? values.filter((v) => matchesSearch(v, q)) : values;
  if (onlySel) filtered = filtered.filter((v) => sel.includes(v));
  const toggle = (v) => {
    if (mode === "single") return commit(sel.includes(v) ? [] : [v]);
    commit(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  };
  return (
    <div className="fw-root h-full w-full flex flex-col px-1 py-0.5 min-h-0" style={vars} data-nodrag data-testid="listbox-filter">
      <FilterTitle widget={widget} className="mb-1" />
      {o.search !== false && (
        <div className="relative mb-1 shrink-0">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid="listbox-filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-7 pl-6 text-xs bg-[color:var(--surface-2)]" />
        </div>
      )}
      <div className="shrink-0">
        <QuickActions mode={mode} count={sel.length} total={values.length} state={visibleState(sel, filtered)}
          exclude={exclude} onExclude={o.exclude === false ? null : ((ex) => commit(sel, ex))}
          onAll={() => commit(visibleState(sel, filtered) === "all" ? clearVisible(sel, filtered) : selectAllVisible(sel, filtered))}
          onClear={() => commit([])}
          onInvert={() => commit(invertVisible(sel, filtered))} />
      </div>
      {o.show_only_selected !== false && sel.length > 0 && (
        <label className="flex items-center gap-1.5 px-1 py-1 text-[10px] text-[color:var(--ink-3)] shrink-0">
          <Checkbox checked={onlySel} onCheckedChange={(v) => setOnlySel(!!v)} className="h-3 w-3" data-testid="listbox-only-selected" />Show only selected
        </label>
      )}
      <div className={cn("fw-dropdown flex-1 min-h-0 overflow-auto p-0.5", o.horizontal && "flex flex-wrap content-start gap-1")}>
        {o.horizontal ? filtered.slice(0, 400).map((v) => {
          const on = sel.includes(v);
          return (
            <button key={String(v)} onClick={() => toggle(v)} data-nodrag data-active={on}
              className="fw-selection !py-1 !px-2 !text-[10px]">
              {valueLabel(v)}
            </button>
          );
        }) : <ValueList values={filtered} sel={sel} mode={mode} toggle={toggle} checkboxes={o.checkboxes !== false} testid="listbox-filter-list" maxH="max-h-full" />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date Picker Filter — single / range / relative / before / after
// ---------------------------------------------------------------------------
const DATE_MODES = [
  { v: "range", l: "Range" }, { v: "single", l: "Single" }, { v: "relative", l: "Relative" },
  { v: "before", l: "Before" }, { v: "after", l: "After" },
];
const iso = (d) => (d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : null);
// Regional date label (dd/MM/yyyy …) for ISO values; falls back to the ISO string.
const showDate = (v) => { const f = getFormatDefaults().dateFormat; return (v && f && formatDateRegional(v, f)) || v; };

function DateBtn({ val, placeholder, onPick, testid, vars }) {
  const [open, setOpen] = useState(false);
  const { firstDay } = getFormatDefaults();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} data-nodrag data-active={!!val} data-iso={val || ""} className="fw-selection flex-1 min-w-0 flex items-center gap-1.5 text-left">
          <CalendarDays className="fw-icon shrink-0" />
          <span className="truncate">{showDate(val) || placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-auto fw-dropdown" style={vars} align="start">
        <Calendar mode="single" weekStartsOn={firstDay} selected={val ? new Date(val) : undefined} defaultMonth={val ? new Date(val) : undefined}
          onSelect={(d) => { onPick(iso(d)); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}

export function DateFilterWidget({ widget, value, onChange }) {
  const o = widget.config?.options || {};
  const allowed = Array.isArray(o.date_modes) && o.date_modes.length ? DATE_MODES.filter((m) => o.date_modes.includes(m.v)) : DATE_MODES;
  const v = value || { mode: o.default_date_mode || allowed[0]?.v || "range" };
  const set = (patch) => onChange({ ...v, ...patch });
  const hasVal = filterWidgetToFilters(widget, v).length > 0;
  const vars = filterStyleVars(o);
  return (
    <div className="fw-root h-full w-full flex flex-col justify-center gap-1.5 px-1" style={vars} data-nodrag data-testid="date-filter-widget">
      <FilterTitle widget={widget} />
      <div className="flex items-center gap-1.5">
        <Select value={v.mode} onValueChange={(m) => onChange({ mode: m })}>
          <SelectTrigger data-testid="date-filter-mode" className="h-7 w-[92px] text-[10px] bg-[color:var(--surface-2)] shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>{allowed.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
        </Select>
        {hasVal && (
          <button data-testid="date-filter-clear" title="Clear" onClick={() => onChange({ mode: v.mode })} className="p-1 rounded hover:bg-[color:var(--surface-2)] shrink-0"><RotateCcw className="fw-icon" /></button>
        )}
      </div>
      {v.mode === "relative" ? (
        <div className="flex items-center gap-1.5">
          <Select value={v.preset || ""} onValueChange={(p) => set({ preset: p })}>
            <SelectTrigger data-testid="date-filter-preset" className="fw-selection h-8 flex-1 text-[11px]"><SelectValue placeholder="Pick a period…" /></SelectTrigger>
            <SelectContent>{DATE_PRESETS.filter((p) => p.value !== "custom").map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          {v.preset === "last_n" && (
            <Input data-testid="date-filter-lastn" type="number" min="1" value={v.n || ""} onChange={(e) => set({ n: e.target.value })} className="h-8 w-16 text-[11px] bg-[color:var(--surface-2)]" placeholder="N" />
          )}
        </div>
      ) : v.mode === "range" ? (
        <div className="flex items-center gap-1.5 min-w-0">
          <DateBtn testid="date-filter-start" val={v.start} placeholder="Start date" onPick={(d) => set({ start: d })} vars={vars} />
          <span className="text-[10px] text-[color:var(--ink-3)] shrink-0">→</span>
          <DateBtn testid="date-filter-end" val={v.end} placeholder="End date" onPick={(d) => set({ end: d })} vars={vars} />
        </div>
      ) : (
        <DateBtn testid="date-filter-start" val={v.start} placeholder={v.mode === "before" ? "Before date…" : v.mode === "after" ? "After date…" : "Pick a date…"} onPick={(d) => set({ start: d })} vars={vars} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspector options editor (selection modes, quick actions, date modes, …)
// ---------------------------------------------------------------------------
export function FilterWidgetOptionsEditor({ widget, opts, patchOpts, fieldLabel = "" }) {
  const t = widget.type;
  const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-2 py-1">
      <Label className="text-xs text-[color:var(--ink-3)]">{label}</Label>
      {children}
    </div>
  );
  const Toggle = ({ k, label, def = true }) => (
    <Row label={label}>
      <Checkbox data-testid={`fw-opt-${k}`} checked={opts[k] !== undefined ? !!opts[k] : def} onCheckedChange={(v) => patchOpts({ [k]: !!v })} />
    </Row>
  );
  const defaultTitle = fieldLabel || humanizeField(widget.config?.dimensions?.[0]?.field || "");
  return (
    <div className="space-y-1">
      <Row label="Title">
        <Input data-testid="fw-opt-label" value={opts.filter_label || ""} onChange={(e) => patchOpts({ filter_label: e.target.value })} placeholder={defaultTitle || "Filter"} className="h-8 w-[150px] text-xs bg-[color:var(--surface)]" />
      </Row>
      <Toggle k="show_label" label="Show title" />
      {t !== "date_picker" && (
        <>
          <Row label="Selection">
            <Select value={opts.selection_mode || (t === "slicer" ? "single" : "multi")} onValueChange={(v) => patchOpts({ selection_mode: v })}>
              <SelectTrigger data-testid="fw-opt-selection" className="h-8 w-[150px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="multi">Multi select</SelectItem>
                <SelectItem value="single">Single select</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row label="Sort values">
            <Select value={opts.sort || "asc"} onValueChange={(v) => patchOpts({ sort: v })}>
              <SelectTrigger data-testid="fw-opt-sort" className="h-8 w-[150px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascending</SelectItem>
                <SelectItem value="desc">Descending</SelectItem>
                <SelectItem value="none">Source order</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row label="Sort as">
            <Select value={opts.sort_kind || "auto"} onValueChange={(v) => patchOpts({ sort_kind: v })}>
              <SelectTrigger data-testid="fw-opt-sort-kind" className="h-8 w-[150px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="period">Months / quarters (chronological)</SelectItem>
                <SelectItem value="weekday">Weekdays</SelectItem>
                <SelectItem value="number">Numbers</SelectItem>
                <SelectItem value="date">Dates</SelectItem>
                <SelectItem value="text">Text (A → Z)</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Toggle k="search" label="Search box" />
          {(t === "combo_box" || t === "slicer") && (
            <>
              <Toggle k="apply_mode" label="Apply button (stage changes)" def={false} />
              <Toggle k="show_clear" label="Inline clear (×) on selection" />
              <Row label="Placeholder">
                <Input data-testid="fw-opt-placeholder" value={opts.placeholder || ""} onChange={(e) => patchOpts({ placeholder: e.target.value })} placeholder="All" className="h-8 w-[150px] text-xs bg-[color:var(--surface)]" />
              </Row>
            </>
          )}
          {t === "slicer" && (
            <Row label="Display">
              <Select value={opts.display || "dropdown"} onValueChange={(v) => patchOpts({ display: v })}>
                <SelectTrigger data-testid="fw-opt-display" className="h-8 w-[150px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="dropdown">Dropdown</SelectItem><SelectItem value="list">Inline list</SelectItem></SelectContent>
              </Select>
            </Row>
          )}
        </>
      )}
      {t === "list_box" && (
        <>
          <Toggle k="checkboxes" label="Checkbox mode" />
          <Toggle k="horizontal" label="Horizontal (chips) layout" def={false} />
          <Toggle k="show_only_selected" label="'Show only selected' option" />
        </>
      )}
      {t === "date_picker" && (
        <Row label="Default mode">
          <Select value={opts.default_date_mode || "range"} onValueChange={(v) => patchOpts({ default_date_mode: v })}>
            <SelectTrigger data-testid="fw-opt-datemode" className="h-8 w-[150px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent>{DATE_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
      )}
      <FilterStyleEditor opts={opts} patchOpts={patchOpts} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item 16: style zones editor — Selection area / Dropdown list / Text / Icon
// ---------------------------------------------------------------------------
const ZONES = [
  { key: "selection", label: "Selection area (the “All” control)" },
  { key: "dropdown", label: "Dropdown / list" },
  { key: "text", label: "Filter title text" },
  { key: "icon", label: "Arrow / clear icon" },
  { key: "buttons", label: "Apply / Clear buttons" },
];

function ColorField({ value, onChange, testid, placeholder = "auto" }) {
  return (
    <div className="flex items-center gap-1">
      <input data-testid={testid} type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)}
        className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-6 w-[88px] text-[10px] font-mono bg-[color:var(--surface)]" />
      {value && <button onClick={() => onChange("")} className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">×</button>}
    </div>
  );
}

function FamilyField({ value, onChange, testid }) {
  return (
    <Select value={value || "__def__"} onValueChange={(v) => onChange(v === "__def__" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-6 w-[124px] text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
      <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.v || "__def__"} value={f.v || "__def__"}>{f.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function NumField({ value, onChange, testid, min = 0, max = 200, w = "w-14" }) {
  return <Input data-testid={testid} type="number" min={min} max={max} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={cn("h-6 text-[10px] bg-[color:var(--surface)]", w)} />;
}

export function FilterStyleEditor({ opts, patchOpts }) {
  const fs = opts.filter_style || {};
  const [zone, setZone] = useState("selection");
  const z = { ...DEFAULT_FILTER_STYLE[zone], ...(fs[zone] || {}) };
  const setZ = (patch) => patchOpts({ filter_style: { ...fs, [zone]: { ...(fs[zone] || {}), ...patch } } });
  const R = ({ label, children }) => (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10px] text-[color:var(--ink-3)]">{label}</span>{children}
    </div>
  );
  return (
    <div data-testid="filter-style-editor" className="mt-2 pt-2 border-t border-[color:var(--glass-border)] space-y-1.5">
      <Label className="text-xs text-[color:var(--ink-2)] font-medium">Filter styling</Label>
      <p className="text-[10px] text-[color:var(--ink-3)]">Independent from the widget background — the selection area stays readable whatever the container colour is.</p>
      <Select value={zone} onValueChange={setZone}>
        <SelectTrigger data-testid="fs-zone" className="h-7 text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
        <SelectContent>{ZONES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      {zone === "selection" && (
        <>
          <R label="Inherit widget background"><Checkbox data-testid="fs-sel-inherit" checked={!!z.inherit} onCheckedChange={(v) => setZ({ inherit: !!v })} /></R>
          {!z.inherit && <R label="Background"><ColorField testid="fs-sel-bg" value={z.bg} onChange={(v) => setZ({ bg: v })} placeholder="white" /></R>}
          <R label="Text"><ColorField testid="fs-sel-text" value={z.text} onChange={(v) => setZ({ text: v })} /></R>
          <R label="Border"><ColorField testid="fs-sel-border" value={z.border} onChange={(v) => setZ({ border: v })} /></R>
          <R label="Border width / style">
            <span className="flex items-center gap-1">
              <NumField testid="fs-sel-bw" value={z.borderWidth} min={0} max={8} onChange={(v) => setZ({ borderWidth: v })} />
              <Select value={z.borderStyle || "solid"} onValueChange={(v) => setZ({ borderStyle: v })}>
                <SelectTrigger data-testid="fs-sel-bstyle" className="h-6 w-[78px] text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{["solid", "dashed", "dotted", "none"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </span>
          </R>
          <R label="Radius"><NumField testid="fs-sel-radius" value={z.radius} max={999} onChange={(v) => setZ({ radius: v })} /></R>
          <R label="Font size / weight">
            <span className="flex items-center gap-1">
              <NumField testid="fs-sel-font" value={z.fontSize} min={8} max={40} onChange={(v) => setZ({ fontSize: v })} />
              <Select value={String(z.weight || 500)} onValueChange={(v) => setZ({ weight: Number(v) })}>
                <SelectTrigger data-testid="fs-sel-weight" className="h-6 w-[78px] text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{[["400", "Regular"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </span>
          </R>
          <R label="Font family"><FamilyField testid="fs-sel-family" value={z.fontFamily} onChange={(v) => setZ({ fontFamily: v })} /></R>
          <R label="Padding"><NumField testid="fs-sel-pad" value={z.padding} max={32} onChange={(v) => setZ({ padding: v })} /></R>
          <R label="Hover bg / text"><span className="flex items-center gap-1"><ColorField testid="fs-sel-hoverbg" value={z.hoverBg} onChange={(v) => setZ({ hoverBg: v })} /></span></R>
          <R label=""><ColorField testid="fs-sel-hovertext" value={z.hoverText} onChange={(v) => setZ({ hoverText: v })} /></R>
          <R label="Active bg / text"><ColorField testid="fs-sel-activebg" value={z.activeBg} onChange={(v) => setZ({ activeBg: v })} /></R>
          <R label=""><ColorField testid="fs-sel-activetext" value={z.activeText} onChange={(v) => setZ({ activeText: v })} /></R>
        </>
      )}
      {zone === "dropdown" && (
        <>
          <R label="Background"><ColorField testid="fs-dd-bg" value={z.bg} onChange={(v) => setZ({ bg: v })} /></R>
          <R label="Text"><ColorField testid="fs-dd-text" value={z.text} onChange={(v) => setZ({ text: v })} /></R>
          <R label="Border"><ColorField testid="fs-dd-border" value={z.border} onChange={(v) => setZ({ border: v })} /></R>
          <R label="Radius"><NumField testid="fs-dd-radius" value={z.radius} max={40} onChange={(v) => setZ({ radius: v })} /></R>
          <R label="Hover bg"><ColorField testid="fs-dd-hoverbg" value={z.hoverBg} onChange={(v) => setZ({ hoverBg: v })} /></R>
          <R label="Selected bg / text"><ColorField testid="fs-dd-activebg" value={z.activeBg} onChange={(v) => setZ({ activeBg: v })} /></R>
          <R label=""><ColorField testid="fs-dd-activetext" value={z.activeText} onChange={(v) => setZ({ activeText: v })} /></R>
          <R label="Checkbox colour"><ColorField testid="fs-dd-check" value={z.checkColor} onChange={(v) => setZ({ checkColor: v })} /></R>
          <R label="Font family"><FamilyField testid="fs-dd-family" value={z.fontFamily} onChange={(v) => setZ({ fontFamily: v })} /></R>
          <R label="Max height"><NumField testid="fs-dd-maxh" value={z.maxHeight} min={80} max={800} onChange={(v) => setZ({ maxHeight: v })} /></R>
        </>
      )}
      {zone === "text" && (
        <>
          <R label="Color"><ColorField testid="fs-text-color" value={z.color} onChange={(v) => setZ({ color: v })} /></R>
          <R label="Font size"><NumField testid="fs-text-font" value={z.fontSize} min={8} max={40} onChange={(v) => setZ({ fontSize: v })} /></R>
          <R label="Weight">
            <Select value={String(z.weight || 500)} onValueChange={(v) => setZ({ weight: Number(v) })}>
              <SelectTrigger data-testid="fs-text-weight" className="h-6 w-[90px] text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>{[["400", "Regular"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </R>
          <R label="Font family"><FamilyField testid="fs-text-family" value={z.fontFamily} onChange={(v) => setZ({ fontFamily: v })} /></R>
        </>
      )}
      {zone === "buttons" && (
        <>
          <p className="text-[10px] text-[color:var(--ink-3)]">Styles Select all / Clear / Invert and the Apply–Cancel row.</p>
          <R label="Background"><ColorField testid="fs-btn-bg" value={z.bg} onChange={(v) => setZ({ bg: v })} placeholder="transparent" /></R>
          <R label="Text"><ColorField testid="fs-btn-text" value={z.text} onChange={(v) => setZ({ text: v })} /></R>
          <R label="Border"><ColorField testid="fs-btn-border" value={z.border} onChange={(v) => setZ({ border: v })} /></R>
          <R label="Radius"><NumField testid="fs-btn-radius" value={z.radius} max={999} onChange={(v) => setZ({ radius: v })} /></R>
          <R label="Font size / weight">
            <span className="flex items-center gap-1">
              <NumField testid="fs-btn-font" value={z.fontSize} min={8} max={24} onChange={(v) => setZ({ fontSize: v })} />
              <Select value={String(z.weight || 500)} onValueChange={(v) => setZ({ weight: Number(v) })}>
                <SelectTrigger data-testid="fs-btn-weight" className="h-6 w-[78px] text-[10px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{[["400", "Regular"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </span>
          </R>
          <R label="Font family"><FamilyField testid="fs-btn-family" value={z.fontFamily} onChange={(v) => setZ({ fontFamily: v })} /></R>
          <R label="Hover bg / text"><ColorField testid="fs-btn-hoverbg" value={z.hoverBg} onChange={(v) => setZ({ hoverBg: v })} /></R>
          <R label=""><ColorField testid="fs-btn-hovertext" value={z.hoverText} onChange={(v) => setZ({ hoverText: v })} /></R>
          <R label="Apply button bg / text"><ColorField testid="fs-btn-primarybg" value={z.primaryBg} onChange={(v) => setZ({ primaryBg: v })} /></R>
          <R label=""><ColorField testid="fs-btn-primarytext" value={z.primaryText} onChange={(v) => setZ({ primaryText: v })} /></R>
        </>
      )}
      {zone === "icon" && (
        <>
          <R label="Color"><ColorField testid="fs-icon-color" value={z.color} onChange={(v) => setZ({ color: v })} /></R>
          <R label="Size"><NumField testid="fs-icon-size" value={z.size} min={8} max={32} onChange={(v) => setZ({ size: v })} /></R>
        </>
      )}
      {(fs[zone] && Object.keys(fs[zone]).length > 0) && (
        <button data-testid="fs-reset-zone" onClick={() => { const n = { ...fs }; delete n[zone]; patchOpts({ filter_style: n }); }} className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">Reset this zone</button>
      )}
    </div>
  );
}
